/**
 * FilePreviewOverlay — full-canvas preview for source files clicked in
 * the leftrail. Browsers can render PDFs and images natively; office
 * docs (.docx, .xlsx) can't be previewed without server-side conversion,
 * so for those we surface a "Open / download" affordance instead.
 *
 * Mounted on top of the design canvas so the user can flip back and
 * forth between the BRD and the agent-drafted document without leaving
 * the design view (and without an extra tab to manage).
 */
import React, { useEffect, useState } from 'react';
import FileTypeBadge from './FileTypeBadge';
import DocxViewer from './preview/DocxViewer';
import JsonViewer from './preview/JsonViewer';
import { presignFile } from '../services/api';

// /design/{id}/presign is the proper "always-fresh" path, but the route
// is currently CFN-orphaned and its JWT authorizer rejects OPTIONS
// preflight, so any browser fetch fails with a Network Error. Fall back
// to the cached `inputs[].downloadUrl` (1h presigned, set on the most
// recent /design/{id} GET) and only attempt /presign as a best-effort
// refresh — never block rendering on it.

const IMG_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']);
const PDF_EXT = new Set(['pdf']);
const HTML_EXT = new Set(['html', 'htm']);
const JSON_EXT = new Set(['json']);
const TEXT_EXT = new Set(['txt', 'md', 'csv']);
const DOCX_EXT = new Set(['docx']);
// xlsx / xls / ppt / pptx / doc render via Microsoft's free Office
// Online embed viewer (`view.officeapps.live.com/op/embed.aspx`). The
// presigned S3 URL is publicly fetchable, so MS servers pull + render
// the file into the iframe. MS occasionally fails — file too big, weird
// CDN response, special chars — so we surface an explicit "Open in new
// tab" affordance alongside it as a manual fallback.
//
// .docx stays on mammoth (DocxViewer) because that renders inline 100%
// of the time; Office Online breaks often enough on S3 presigned URLs
// that the in-canvas highlight-and-ask-the-agent workflow couldn't rely
// on it.
const OFFICE_EXT = new Set(['doc', 'xls', 'xlsx', 'ppt', 'pptx']);

function officeViewerUrl(fileUrl) {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
}

function extOf(name) {
  const m = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
}

export default function FilePreviewOverlay({ file, onClose, onDelete }) {
  // Render against the cached presigned URL immediately when we have one
  // (instant open), and fire /presign concurrently as a refresh OR as the
  // PRIMARY source when the cached URL is missing — files uploaded mid-
  // session don't yet have a cached `downloadUrl` because the design row
  // hasn't been re-fetched. Previously this combination forced the user
  // to hit Reload; now we just call /presign inline and show a brief
  // loading state, only surfacing the fallback if /presign itself fails.
  const [freshUrl, setFreshUrl] = useState(null);
  const [presignError, setPresignError] = useState(false);
  const [presignLoading, setPresignLoading] = useState(false);

  useEffect(() => {
    if (!file) return undefined;
    setFreshUrl(null);
    setPresignError(false);
    setPresignLoading(false);
    let cancelled = false;
    if (file.designId && file.s3Key) {
      setPresignLoading(true);
      presignFile(file.designId, file.s3Key)
        .then((res) => {
          if (cancelled) return;
          if (res?.url) {
            setFreshUrl(res.url);
            setPresignError(false);
          } else {
            setPresignError(true);
          }
        })
        .catch(() => {
          if (cancelled) return;
          setPresignError(true);
        })
        .finally(() => {
          if (!cancelled) setPresignLoading(false);
        });
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelled = true;
      document.removeEventListener('keydown', onKey);
    };
  }, [file, onClose]);

  if (!file) return null;
  const ext = extOf(file.name);
  const url = freshUrl || file.downloadUrl || '';
  // Only show the "loading" placeholder when we have NEITHER a cached URL
  // nor a fresh one AND a /presign call is still in flight. With a cached
  // downloadUrl we render the file immediately and let the background
  // refresh swap to the fresher URL silently.
  const isLoading = !url && presignLoading;

  let body = null;
  if (isLoading) {
    body = (
      <div className="file-preview-fallback">
        <div className="file-preview-fallback-icon">
          <FileTypeBadge name={file.name} />
        </div>
        <div className="file-preview-fallback-title">{file.name}</div>
        <div className="file-preview-fallback-msg">Loading…</div>
      </div>
    );
  } else if (!url) {
    body = (
      <div className="file-preview-fallback">
        <div className="file-preview-fallback-icon">
          <FileTypeBadge name={file.name} />
        </div>
        <div className="file-preview-fallback-title">{file.name}</div>
        <div className="file-preview-fallback-msg">
          {presignError
            ? "Couldn't fetch a download link for this file. It may have been deleted or you may not have access."
            : "This file's download link hasn't been generated yet."}
        </div>
        <div className="file-preview-fallback-actions">
          <button
            type="button"
            className="modal-btn modal-btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    );
  } else if (IMG_EXT.has(ext)) {
    body = (
      <img
        src={url}
        alt={file.name}
        className="file-preview-image"
      />
    );
  } else if (PDF_EXT.has(ext) || HTML_EXT.has(ext)) {
    body = (
      <iframe
        src={url}
        title={file.name}
        className="file-preview-frame"
      />
    );
  } else if (JSON_EXT.has(ext)) {
    // Render JSON in our DOM (fetch + pretty-print). Used to be served
    // in a cross-origin iframe, which silently rendered as an empty
    // page on the presigned-URL response in some browsers.
    body = <JsonViewer url={url} fileName={file.name} />;
  } else if (TEXT_EXT.has(ext)) {
    // Inline text in an iframe so the browser handles encoding + scrolling.
    body = (
      <iframe
        src={url}
        title={file.name}
        className="file-preview-frame"
      />
    );
  } else if (DOCX_EXT.has(ext)) {
    body = <DocxViewer url={url} fileName={file.name} />;
  } else if (OFFICE_EXT.has(ext)) {
    body = (
      <iframe
        src={officeViewerUrl(url)}
        title={file.name}
        className="file-preview-frame"
      />
    );
  } else {
    body = (
      <div className="file-preview-fallback">
        <div className="file-preview-fallback-icon">
          <FileTypeBadge name={file.name} />
        </div>
        <div className="file-preview-fallback-title">{file.name}</div>
        <div className="file-preview-fallback-msg">
          Preview not available for this file type.
        </div>
        <div className="file-preview-fallback-actions">
          <a
            className="modal-btn modal-btn-primary"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            download={file.name}
          >
            Download
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="file-preview-overlay">
      {/* Top header (Download / Close) removed per UX feedback —
       * download + delete now live on the per-file right-click menu in
       * the sidebar tree. Esc still closes the overlay; a single × in
       * the corner stays as a discoverable dismiss affordance. */}
      {/* Delete moved to the per-file right-click menu in the sidebar
          tree — keeps file-level actions on the file row itself. */}
      <button
        type="button"
        className="file-preview-dismiss"
        onClick={onClose}
        aria-label="Close preview"
        title="Close preview (Esc)"
      >×</button>
      <div className="file-preview-body">
        {body}
      </div>
    </div>
  );
}
